import React, { useState, useEffect, useContext } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { withRouter } from 'react-router-dom';
import OktaService from '../../services/OktaService';
import { AlertBoxContext } from '../context/AlertBoxContext';
import loginBg from '../../assets/login-bg.webp';
import logo from '../../assets/logo.webp';
import loginIllustration from '../../assets/login-illustration.webp';
import AppButton from '../components/AppButton';

const LoginPageMui = props => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setLoading] = useState(false);
  const { setAlert } = useContext(AlertBoxContext);
  const theme = useTheme();

  useEffect(() => {
    const bg = new Image();
    const illustration = new Image();
    bg.src = loginBg;
    illustration.src = loginIllustration;
  }, []);

  const handleOktaLogin = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      await OktaService.login();
    } catch (error) {
      console.error(error);
      setLoading(false);
      setAlert({
        open: true,
        message: 'Unable to start Okta login. Please try again.',
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        backgroundImage: `url(${loginBg})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha(theme.palette.background.paper, 0.65),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          mt: { xs: 2, md: 4 },
          mr: { xs: 2, md: 4 },
          ml: { xs: 2, md: 0 },
          alignItems: { xs: 'stretch', md: 'flex-end' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 580,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Audience-360"
            sx={{
              height: 25,
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
              alignSelf: 'flex-start',
              ml: 0,
            }}
          />

          <Typography
            variant="h3"
            sx={{
              mt: { xs: 4, md: 10 },
              color: 'primary.dark',
              fontWeight: theme.typography.fontWeightMedium,
              fontSize: '36px',
              lineHeight: '48px',
            }}
          >
            Welcome to Audience - 360
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontStyle: 'italic',
              color: 'text.primary',
              fontSize: '18px',
              lineHeight: '26px',
            }}
          >
            Easily upload, configure, and integrate your data from multiple
            sources. Audience-360 streamlines data management, automating
            workflows and providing the flexibility to meet your business needs.
          </Typography>

          <Paper
            elevation={1}
            sx={{
              p: 3,
              mt: 5,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderStyle: 'solid',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: 'primary.dark',
                fontWeight: theme.typography.h6.fontWeight,
                fontSize: { xs: '1.375rem', md: '1.5rem' },
                lineHeight: 1.25,
              }}
            >
              Sign in with Okta
            </Typography>

            {errorMessage ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'error.main',
                  fontWeight: theme.typography.h6.fontWeight,
                  mb: 1.5,
                }}
              >
                {errorMessage}
              </Typography>
            ) : null}

            <Typography variant="body2" sx={{ mb: 2.5, color: 'text.secondary' }}>
              Use your organization account. Password reset and stay signed-in
              are managed in Okta.
            </Typography>

            <AppButton
              type="button"
              id="login-button"
              disabled={isLoading}
              onClick={handleOktaLogin}
            >
              Sign in with Okta
            </AppButton>
          </Paper>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'block' },
          backgroundImage: `url(${loginIllustration})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPositionX: 'right',
          position: 'relative',
          zIndex: 1,
        }}
      />
    </Box>
  );
};

export default withRouter(LoginPageMui);
